import { GameContext, InPlay, PeriodScoresResult, ScopeCompletedResolver } from '../../interfaces';
import { MarketOutcome, PeriodType } from '../../enums';
import {
	extractNumber,
	extractPlayers,
	extractSelection,
	getPlayerResult,
	getPlayerStats,
	resolveCondition,
	sumPlayerPeriodsStat,
	sumTeamPeriodsStat,
} from '../parse-market.utils';
import { calculateTimeProgress } from './basketball.utils';

// NFL + NCAAF both play 4 15-minute quarters with 2 halves and possible
// overtime. Structurally identical to NBA (4 quarters + halves + OT), so the
// scope resolver mirrors nbaScopeResolver — half/quarter completion is
// determined by comparing OpticOdds' in-play period number to the endPeriod
// for the requested scope. Verified against OpticOdds NFL statistics guide
// 2026-07-20: scores.home.periods uses period_1/period_2/period_3/period_4
// for regulation quarters, period_5 for overtime (same convention as
// basketball).
const buildFootballScopeResolver =
	(): ScopeCompletedResolver =>
	(period, { isCompleted, inPlay }) => {
		if (isCompleted) return true;

		const currentPeriod = Number(inPlay?.period ?? 0);
		const clock = inPlay?.clock ?? null;

		if (!currentPeriod) return false;

		let endPeriod = 0;

		switch (period) {
			case PeriodType.Game:
				return isCompleted;
			case PeriodType.Half1:
				endPeriod = 2;
				break;
			case PeriodType.Half2:
				endPeriod = 4;
				break;
			case PeriodType.Period1:
				endPeriod = 1;
				break;
			case PeriodType.Period2:
				endPeriod = 2;
				break;
			case PeriodType.Period3:
				endPeriod = 3;
				break;
			case PeriodType.Period4:
				endPeriod = 4;
				break;
		}

		if (currentPeriod > endPeriod) return true;

		if (currentPeriod === endPeriod) {
			return clock === '00:00' || clock === null;
		}

		return false;
	};

export const nflScopeResolver: ScopeCompletedResolver = buildFootballScopeResolver();
export const ncaafScopeResolver: ScopeCompletedResolver = buildFootballScopeResolver();

// NFL regulation: 4×15-min quarters = 60 min. Overtime is 10 min (regular
// season) or 15 min (playoffs) — 10 is the safer default since the majority
// of OT games happen in regular season. NCAAF regulation is also 4×15-min,
// with OT structured as untimed alternating possessions (drives-based, not
// clock-based) — for the time-progress bar, treating NCAAF OT as 10 min is
// close enough for the UI (progress bars degrade gracefully past 1.0 anyway).
export const nflTimeProgressResolver = (inPlay: InPlay) =>
	calculateTimeProgress(inPlay, { periodMinutes: 15, regulationPeriods: 4, overtimeMinutes: 10 });

export const ncaafTimeProgressResolver = (inPlay: InPlay) =>
	calculateTimeProgress(inPlay, { periodMinutes: 15, regulationPeriods: 4, overtimeMinutes: 10 });

// ────────────────────────────────────────────────────────────
// Shared resolution helpers (dedupe of near-identical blocks previously
// copy-pasted across football.config.ts).
// ────────────────────────────────────────────────────────────

/** Sums a per-player stat across periods for one team, or both teams combined ('both'). */
export const resolveTeamTotalStat = (
	r: any,
	gc: GameContext,
	team: string | 'both',
	periods: string[],
	statKey: string,
	threshold: number,
	completed: boolean
): [MarketOutcome, number] => {
	const total =
		team === 'both'
			? sumTeamPeriodsStat(r, gc.homeTeam, periods, statKey) +
				sumTeamPeriodsStat(r, gc.awayTeam, periods, statKey)
			: sumTeamPeriodsStat(r, team, periods, statKey);

	return [resolveCondition(total >= threshold, completed), total];
};

export const resolveTeamTotalOddEven = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const team = extractSelection(q, 'Will ', ["'s total"]);
	const { home, away, isCompleted } = getPeriodScores(gc, period);
	const teamTotal = team === gc.homeTeam ? home : away;

	if (!isCompleted) return [MarketOutcome.Pending];

	return [teamTotal % 2 === 0 ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveCorrectScore = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const match = q.match(/(\d+)[-:](\d+)/);
	if (!match) return [MarketOutcome.Lost];

	const [expectedHome, expectedAway] = match.slice(1).map(Number);

	return [home === expectedHome && away === expectedAway ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveComboPlayerStat = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	const [playerA, playerB] = extractPlayers(q);
	const total =
		(getPlayerResult(playerA, r)?.[statKey] ?? 0) + (getPlayerResult(playerB, r)?.[statKey] ?? 0);

	return [resolveCondition(total >= threshold, gc.isCompleted), total];
};

/** actionPhrase is inlined into a `Will <A> <actionPhrase> <B>?` regex — pass it pre-escaped for regex use. */
export const resolveH2HPlayerStat = (
	q: string,
	_gc: GameContext,
	r: any,
	statKey: string,
	actionPhrase: string
): [MarketOutcome] => {
	const regex = new RegExp(`^Will (.+) ${actionPhrase} (.+)\\?$`);
	const match = q.match(regex);
	if (!match) throw new Error(`Cannot extract players from question: ${q}`);

	const [, playerA, playerB] = match;
	const a = getPlayerResult(playerA, r)?.[statKey] ?? 0;
	const b = getPlayerResult(playerB, r)?.[statKey] ?? 0;

	return [a > b ? MarketOutcome.Won : MarketOutcome.Lost];
};

/**
 * H2H comparison with a trailing yard handicap applied to playerA before comparing, e.g.
 * "Will A throw for more passing yards than B with a -10.5 yard handicap?".
 */
export const resolveH2HPlayerStatSpread = (
	q: string,
	_gc: GameContext,
	r: any,
	statKey: string,
	actionPhrase: string
): [MarketOutcome] => {
	const regex = new RegExp(
		`^Will (.+) ${actionPhrase} (.+) with a ([+-]?[\\d.]+) yard handicap\\?$`
	);
	const match = q.match(regex);
	if (!match) throw new Error(`Cannot extract players from question: ${q}`);

	const [, playerA, playerB, handicapStr] = match;
	const handicap = Number(handicapStr);
	const a = getPlayerResult(playerA, r)?.[statKey] ?? 0;
	const b = getPlayerResult(playerB, r)?.[statKey] ?? 0;

	return [a + handicap > b ? MarketOutcome.Won : MarketOutcome.Lost];
};

/**
 * Generalizes the Anytime-TD-Scorer / period Player-Touchdowns family: sums a per-period player
 * stat across `periods` and compares to a threshold. Pass `fixedThreshold` (e.g. 1) for
 * "Anytime"-style markets whose question has no numeric threshold to extract.
 */
export const resolvePlayerPeriodThreshold = (
	q: string,
	gc: GameContext,
	r: any,
	periods: string[],
	scopePeriod: PeriodType,
	statKey: string,
	extractAfter: string[],
	fixedThreshold?: number
): [MarketOutcome, number] => {
	const threshold = fixedThreshold ?? extractNumber(q);
	const playerName = extractSelection(q, 'Will ', extractAfter);
	const value = sumPlayerPeriodsStat(playerName, r, periods, statKey);

	return [resolveCondition(value >= threshold, gc.isScopeCompleted(scopePeriod)), value];
};

export const resolveLongestPlayerStat = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string,
	source: 'market_stats' | 'period_stats' = 'market_stats'
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	// extractSelection can't be used here: its suffix match requires a `\s` before the suffix
	// literal, but the possessive "'s longest" attaches directly to the name with no space
	// (e.g. "Will Kyren Williams's longest reception..."), so it never matched.
	const playerName = q.match(/^Will (.+?)'s longest/)?.[1] ?? '';
	const value =
		source === 'period_stats'
			? (getPlayerStats(playerName, r)?.[statKey] ?? 0)
			: (getPlayerResult(playerName, r)?.[statKey] ?? 0);

	return [resolveCondition(value >= threshold, gc.isCompleted), value];
};

// ────────────────────────────────────────────────────────────
// New helpers for markets that exist in real OO payloads but had no config
// entry at all (see JWB audit: NFL markets.json has ~16 market families with
// zero entry in football.config.ts).
// ────────────────────────────────────────────────────────────

/** OR semantics across a pair of players: Won if either player's stat meets the threshold. */
export const resolveEitherPlayerStat = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string,
	actionPhrase: string,
	threshold = extractNumber(q) || 1
): [MarketOutcome, number] => {
	const regex = new RegExp(`^Will (.+) or (.+) ${actionPhrase}\\?$`);
	const match = q.match(regex);
	if (!match) throw new Error(`Cannot extract players from question: ${q}`);

	const [, playerA, playerB] = match;
	const a = getPlayerResult(playerA, r)?.[statKey] ?? 0;
	const b = getPlayerResult(playerB, r)?.[statKey] ?? 0;
	const total = Math.max(a, b);

	return [resolveCondition(a >= threshold || b >= threshold, gc.isCompleted), total];
};

/**
 * AND semantics: Won only if every period group in `periodGroups` individually meets the
 * threshold (e.g. Each Quarter → four single-period groups, Each Half → two two-period groups).
 */
export const resolveEachPeriodThreshold = (
	q: string,
	gc: GameContext,
	r: any,
	periodGroups: string[][],
	statKey: string,
	extractAfter: string[]
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	const playerName = extractSelection(q, 'Will ', extractAfter);
	const values = periodGroups.map((periods) =>
		sumPlayerPeriodsStat(playerName, r, periods, statKey)
	);
	const minValue = Math.min(...values);

	return [
		resolveCondition(
			values.every((v) => v >= threshold),
			gc.isCompleted
		),
		minValue,
	];
};

/**
 * NFL halftime (end of Q2, `PeriodType.Half1`) is not the same scope as soccer's "1st half"
 * (`PeriodType.Period1`) — soccer plays 2×45, football plays 4×15 with halftime after Q2.
 * String parsing / draw-handling mirrors `soccer.utils.ts`'s `resolveHalftimeFulltime`.
 */
export const resolveHalftimeFulltime = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext
): [MarketOutcome] => {
	const match = q.match(/Will the game be (.+?) at halftime and (.+?) at full time\?/);
	if (!match) return [MarketOutcome.Pending];

	const normalize = (pick: string): string => (pick === 'a draw' ? 'Draw' : pick);
	const htPick = normalize(match[1].trim());
	const ftPick = normalize(match[2].trim());

	const { home: htHome, away: htAway, isCompleted: htDone } = getPeriodScores(gc, PeriodType.Half1);

	if (!htDone) return [MarketOutcome.Pending];

	const actualHT = htHome === htAway ? 'Draw' : htHome > htAway ? gc.homeTeam : gc.awayTeam;

	if (actualHT !== htPick) return [MarketOutcome.Lost];

	if (!gc.isCompleted) return [MarketOutcome.Pending];

	const actualFT =
		gc.homeScore === gc.awayScore
			? 'Draw'
			: gc.homeScore > gc.awayScore
				? gc.homeTeam
				: gc.awayTeam;

	return [actualFT === ftPick ? MarketOutcome.Won : MarketOutcome.Lost];
};
